import { useRef, useState, type ReactNode, type ComponentType, type SVGProps } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  PlusIcon,
  PaperAirplaneIcon,
  DocumentTextIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PaperClipIcon,
  PencilSquareIcon,
  TrashIcon,
  InformationCircleIcon,
  DocumentArrowUpIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { Modal, ConfirmModal } from '../../components/common';
import {
  loteAfiliacionService,
  type AltaFichaPayload,
  type EdicionFichaPayload,
  type FichaLote,
  type FilaRechazadaLote,
  type LoteAfiliacion,
} from '../../services/api';
import { formatFechaCalendario } from '../../utils/fechas';
import { ESTADO_LOTE, RESULTADO_FICHA, numeroCorto } from '../../utils/lotesAfiliacion';

type Icono = ComponentType<SVGProps<SVGSVGElement>>;

type Filtro = 'TODAS' | 'CON_OBSERVACIONES' | 'SIN_OBSERVACIONES' | 'CONSTANCIA';

const FILTROS: Record<Filtro, { etiqueta: string; aplica: (f: FichaLote) => boolean }> = {
  TODAS: { etiqueta: 'Todas', aplica: () => true },
  CON_OBSERVACIONES: { etiqueta: 'Con observaciones', aplica: f => f.conObservaciones },
  SIN_OBSERVACIONES: { etiqueta: 'Sin observaciones', aplica: f => !f.conObservaciones },
  CONSTANCIA: { etiqueta: 'Requieren constancia', aplica: f => f.admiteConstancia && !f.constanciaAdjunta },
};

function formatearDni(dni: string): string {
  return dni.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function descargarExcel(lote: LoteAfiliacion) {
  const filas = lote.fichas.map(f => ({
    DNI: f.dni,
    Apellido: f.apellido,
    Nombre: f.nombre,
    'Fecha de la ficha': formatFechaCalendario(f.fechaAfiliacion),
    'Resultado del control': f.conObservaciones ? 'Con observaciones' : f.mensajes.length > 0 ? 'Duplicado válido' : 'Sin observaciones',
    Observaciones: f.mensajes.join(' | '),
    'Constancia de renuncia': f.admiteConstancia ? (f.constanciaAdjunta ? 'Adjunta' : 'No adjunta') : '',
  }));
  const hoja = XLSX.utils.json_to_sheet(filas);
  hoja['!cols'] = [{ wch: 12 }, { wch: 22 }, { wch: 22 }, { wch: 16 }, { wch: 20 }, { wch: 90 }, { wch: 22 }];
  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, hoja, 'Fichas presentadas');
  XLSX.writeFile(libro, `lote-${numeroCorto(lote.numero)}-presentado.xlsx`);
}

export default function LoteAfiliacionPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inputExcel = useRef<HTMLInputElement>(null);
  const [rechazadas, setRechazadas] = useState<FilaRechazadaLote[]>([]);
  const [fichaEditando, setFichaEditando] = useState<FichaLote | null>(null);
  const [fichaAQuitar, setFichaAQuitar] = useState<FichaLote | null>(null);
  const [altaAbierta, setAltaAbierta] = useState(false);
  const [confirmarPresentar, setConfirmarPresentar] = useState(false);
  const [filtro, setFiltro] = useState<Filtro>('TODAS');
  const [confirmarDescartar, setConfirmarDescartar] = useState(false);

  const { data: lote, isLoading } = useQuery({
    queryKey: ['lote-afiliacion', id],
    queryFn: () => loteAfiliacionService.detalle(id),
  });

  const actualizar = (nuevo: LoteAfiliacion) => {
    queryClient.setQueryData(['lote-afiliacion', id], nuevo);
    queryClient.invalidateQueries({ queryKey: ['lotes-afiliacion'] });
  };

  const cargarExcel = useMutation({
    mutationFn: (archivo: File) => loteAfiliacionService.cargarExcel(id, archivo),
    onSuccess: (r) => {
      actualizar(r.lote);
      setRechazadas(r.rechazadas);
      toast.success(`Se cargaron ${r.cargadas} fichas.`);
    },
  });

  const quitar = useMutation({
    mutationFn: (fichaId: string) => loteAfiliacionService.quitarFicha(fichaId),
    onSuccess: (l) => {
      actualizar(l);
      setFichaAQuitar(null);
    },
  });

  const constancia = useMutation({
    mutationFn: ({ fichaId, archivo }: { fichaId: string; archivo: File }) =>
      loteAfiliacionService.adjuntarConstancia(fichaId, archivo),
    onSuccess: (l) => {
      actualizar(l);
      toast.success('Constancia adjuntada.');
    },
  });

  const presentar = useMutation({
    mutationFn: () => loteAfiliacionService.presentar(id),
    onSuccess: (l) => {
      actualizar(l);
      setConfirmarPresentar(false);
      setFiltro('TODAS');
      toast.success('Lote presentado. Recordá entregar las fichas en papel en la SEP.');
    },
  });

  const descartar = useMutation({
    mutationFn: () => loteAfiliacionService.descartar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lotes-afiliacion'] });
      toast.success('Borrador descartado.');
      navigate('/lotes-afiliacion');
    },
  });
  if (isLoading) return <p className="p-6 text-sm text-gray-500">Cargando…</p>;
  if (!lote) return <p className="p-6 text-sm text-gray-500">No encontramos este lote.</p>;

  const estado = ESTADO_LOTE[lote.estado];
  const borrador = lote.estado === 'BORRADOR';
  const enEspera = lote.estado === 'EN_ESPERA_SEP';
  const { total, sinObservaciones = 0, conObservaciones = 0, requierenConstancia = 0 } = lote.resumen;
  const elegirExcel = () => inputExcel.current?.click();
  const filtrar = (f: Filtro) => (lote.fichas.length > 0 ? () => setFiltro(f) : undefined);

  return (
    <div className="space-y-6 p-6">
      <input
        ref={inputExcel}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={(e) => {
          const archivo = e.target.files?.[0];
          if (archivo) cargarExcel.mutate(archivo);
          e.target.value = '';
        }}
      />

      <button
        onClick={() => navigate('/lotes-afiliacion')}
        className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-primary-600"
      >
        <ArrowLeftIcon className="h-4 w-4" /> Mis lotes
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">Lote N° {numeroCorto(lote.numero)}</h1>
            <span className={estado.clase}>{estado.etiqueta}</span>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-500">
            <span className="inline-flex items-center gap-1.5">
              <DocumentTextIcon className="h-4 w-4" />
              {lote.archivoNombre ?? 'Sin archivo cargado'}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CalendarIcon className="h-4 w-4" />
              {lote.fechaPresentacion
                ? `Presentado el ${formatFechaCalendario(lote.fechaPresentacion)}`
                : 'La fecha de presentación se fija al presentar'}
            </span>
          </div>
        </div>

        {borrador && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setConfirmarDescartar(true)}
              className="btn-secondary gap-2 text-red-600 hover:bg-red-50"
            >
              <TrashIcon className="h-5 w-5" /> Descartar
            </button>
            <button onClick={elegirExcel} disabled={cargarExcel.isPending} className="btn-secondary gap-2">
              <ArrowUpTrayIcon className="h-5 w-5" />
              {cargarExcel.isPending ? 'Cargando…' : 'Cargar Excel'}
            </button>
            <button onClick={() => setAltaAbierta(true)} className="btn-secondary gap-2">
              <PlusIcon className="h-5 w-5" /> Agregar ficha
            </button>
            <button onClick={() => setConfirmarPresentar(true)} disabled={total === 0} className="btn-primary gap-2">
              <PaperAirplaneIcon className="h-5 w-5" /> Presentar lote ({total})
            </button>
          </div>
        )}
        
        {!borrador && lote.fichas.length > 0 && (
          <button onClick={() => descargarExcel(lote)} className="btn-secondary gap-2">
            <ArrowDownTrayIcon className="h-5 w-5" /> Descargar Excel
          </button>
        )}
      </div>

      {!enEspera && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Contador icono={ClipboardDocumentListIcon} valor={total} texto={borrador ? 'fichas cargadas' : 'fichas presentadas'} tono="primary"
          activo={filtro === 'TODAS'} onClick={filtrar('TODAS')} />
        <Contador icono={CheckCircleIcon} valor={sinObservaciones} texto="sin observaciones" tono="green"
          activo={filtro === 'SIN_OBSERVACIONES'} onClick={filtrar('SIN_OBSERVACIONES')} />
        <Contador icono={ExclamationTriangleIcon} valor={conObservaciones} texto="con observaciones" tono="amber"
          activo={filtro === 'CON_OBSERVACIONES'} onClick={filtrar('CON_OBSERVACIONES')} />
        <Contador icono={PaperClipIcon} valor={requierenConstancia} texto="requieren constancia de renuncia" tono="gray"
          activo={filtro === 'CONSTANCIA'} onClick={filtrar('CONSTANCIA')} />
      </div>
    )}

      {rechazadas.length > 0 && <PanelRechazadas filas={rechazadas} onCerrar={() => setRechazadas([])} />}

      {!borrador && (
        <>
          <div className="alert-info flex gap-3 text-sm">
            <InformationCircleIcon className="h-5 w-5 flex-shrink-0" />
            <div className="space-y-1">
            {lote.fechaRecepcionFichas ? (
                <p className="font-medium">
                  La SEP recibió tus fichas en papel el {formatFechaCalendario(lote.fechaRecepcionFichas)}.
                </p>
              ) : (
                <p className="font-medium">
                  Recordá entregar las {total} fichas en papel en la SEP, identificadas con el Lote N° {numeroCorto(lote.numero)}.
                </p>
              )}
              {estado.descripcion && <p>{estado.descripcion}</p>}
            </div>
          </div>
          <TablaFichas
            soloLectura
            sinContadores={enEspera}
            fichas={lote.fichas}
            filtro={filtro}
            onFiltro={setFiltro}
            onEditar={setFichaEditando}
            onQuitar={setFichaAQuitar}
            onConstancia={(fichaId, archivo) => constancia.mutate({ fichaId, archivo })}
            subiendoConstancia={constancia.isPending}
          />
        </>
      )}

      {borrador && (
        <>
          {lote.fichas.length === 0 ? (
            <ZonaCarga
              cargando={cargarExcel.isPending}
              onElegir={elegirExcel}
              onSoltar={(archivo) => cargarExcel.mutate(archivo)}
              onAlta={() => setAltaAbierta(true)}
            />
          ) : (
            <TablaFichas
              fichas={lote.fichas}
              filtro={filtro}
              onFiltro={setFiltro}
              onEditar={setFichaEditando}
              onQuitar={setFichaAQuitar}
              onConstancia={(fichaId, archivo) => constancia.mutate({ fichaId, archivo })}
              subiendoConstancia={constancia.isPending}
            />
          )}
          <div className="alert-info flex gap-3 text-sm">
            <InformationCircleIcon className="h-5 w-5 flex-shrink-0" />
            <p>
              Se presentan todas las fichas del lote, con sus observaciones, correcciones y constancias. Presentar el
              lote no incorpora a nadie al registro: queda en espera hasta que la SEP valide las fichas en papel.
            </p>
          </div>
        </>
      )}

      {fichaEditando && (
        <ModalEdicion
          ficha={fichaEditando}
          onCerrar={() => setFichaEditando(null)}
          onGuardado={(l) => {
            actualizar(l);
            setFichaEditando(null);
          }}
        />
      )}

      {altaAbierta && (
        <ModalAlta
          loteId={id}
          onCerrar={() => setAltaAbierta(false)}
          onGuardado={(l) => {
            actualizar(l);
            setAltaAbierta(false);
          }}
        />
      )}

      <ConfirmModal
        isOpen={confirmarPresentar}
        onClose={() => setConfirmarPresentar(false)}
        onConfirm={() => presentar.mutate()}
        title="Presentar lote"
        message={`Se van a presentar ${total} fichas (${conObservaciones} con observaciones). Después de presentar ya no vas a poder modificar el lote.`}
        confirmText="Presentar"
        variant="info"
        loading={presentar.isPending}
      />

      <ConfirmModal
        isOpen={!!fichaAQuitar}
        onClose={() => setFichaAQuitar(null)}
        onConfirm={() => fichaAQuitar && quitar.mutate(fichaAQuitar.id)}
        title="Quitar ficha"
        message={`¿Quitar a ${fichaAQuitar?.apellido}, ${fichaAQuitar?.nombre} del lote?`}
        confirmText="Quitar"
        loading={quitar.isPending}
      />
      
      <ConfirmModal
        isOpen={confirmarDescartar}
        onClose={() => setConfirmarDescartar(false)}
        onConfirm={() => descartar.mutate()}
        title="Descartar borrador"
        message={`El lote y sus ${total} fichas van a dejar de aparecer en tu lista y ya no se van a controlar. No se puede deshacer.`}
        confirmText="Descartar"
        loading={descartar.isPending}
      />
    </div>
  );
}

const TONOS = {
  primary: 'bg-primary-50 text-primary-600',
  green: 'bg-green-50 text-green-600',
  amber: 'bg-amber-50 text-amber-600',
  gray: 'bg-gray-100 text-gray-500',
};

function Contador({ icono: IconoContador, valor, texto, tono, activo = false, onClick }: {
  icono: Icono;
  valor: number;
  texto: string;
  tono: keyof typeof TONOS;
  activo?: boolean;
  onClick?: () => void;
}) {
  const contenido = (
    <>
      <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full ${TONOS[tono]}`}>
        <IconoContador className="h-6 w-6" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{valor}</p>
        <p className="text-sm text-gray-500">{texto}</p>
      </div>
    </>
  );
  const base = 'card flex w-full items-center gap-4 px-5 py-4 text-left';
  if (!onClick) return <div className={base}>{contenido}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} transition hover:border-primary-300 hover:shadow-md ${activo ? 'border-transparent ring-2 ring-primary-500' : ''}`}
    >
      {contenido}
    </button>
  );
}

function ZonaCarga({ cargando, onElegir, onSoltar, onAlta }: {
  cargando: boolean;
  onElegir: () => void;
  onSoltar: (archivo: File) => void;
  onAlta: () => void;
}) {
  const [arrastrando, setArrastrando] = useState(false);
  return (
    <div
      onClick={onElegir}
      onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
      onDragLeave={() => setArrastrando(false)}
      onDrop={(e) => {
        e.preventDefault();
        setArrastrando(false);
        const archivo = e.dataTransfer.files?.[0];
        if (archivo) onSoltar(archivo);
      }}
      className={`flex cursor-pointer flex-col items-center rounded-xl border-2 border-dashed px-6 py-12 text-center transition ${
        arrastrando ? 'border-primary-500 bg-primary-50' : 'border-gray-300 bg-white hover:border-primary-300 hover:bg-gray-50'
      }`}
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-50">
        <DocumentArrowUpIcon className="h-7 w-7 text-primary-600" />
      </div>
      <p className="mt-4 font-medium text-gray-900">
        {cargando ? 'Cargando el archivo…' : 'Arrastrá el Excel de afiliados o hacé clic para elegirlo'}
      </p>
      <p className="mt-1 text-sm text-gray-500">Usá la misma planilla del padrón de afiliados (.xlsx).</p>
      <button
        onClick={(e) => { e.stopPropagation(); onAlta(); }}
        className="mt-4 text-sm font-medium text-primary-600 hover:text-primary-700"
      >
        o agregá las fichas de a una
      </button>
    </div>
  );
}

function PanelRechazadas({ filas, onCerrar }: { filas: FilaRechazadaLote[]; onCerrar: () => void }) {
  return (
    <div className="alert-danger text-sm">
      <div className="flex items-start justify-between gap-3">
        <p className="flex items-center gap-2 font-medium">
          <ExclamationTriangleIcon className="h-5 w-5" />
          {filas.length} {filas.length === 1 ? 'fila del Excel no se pudo cargar' : 'filas del Excel no se pudieron cargar'}
        </p>
        <button onClick={onCerrar} title="Cerrar" className="rounded p-1 hover:bg-red-100">
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
      <ul className="mt-2 space-y-1 pl-7">
        {filas.map((f, i) => (
          <li key={i}>
            <span className="font-medium">{f.dni ?? 'Sin DNI'}</span>
            {' · '}
            {[f.apellido, f.nombre].filter(Boolean).join(', ') || 'Sin nombre'}: {f.motivos.join('; ')}
          </li>
        ))}
      </ul>
    </div>
  );
}

interface PropsTabla {
  soloLectura?: boolean;
  sinContadores?: boolean;
  fichas: FichaLote[];
  filtro: Filtro;
  onFiltro: (f: Filtro) => void;
  onEditar: (ficha: FichaLote) => void;
  onQuitar: (ficha: FichaLote) => void;
  onConstancia: (fichaId: string, archivo: File) => void;
  subiendoConstancia: boolean;
}

type PropsFila = Pick<PropsTabla, 'soloLectura' | 'onEditar'| 'onQuitar' | 'onConstancia' | 'subiendoConstancia'> & { ficha: FichaLote };

function TablaFichas({ fichas, filtro, onFiltro, sinContadores = false, ...acciones }: PropsTabla) {
  const [busqueda, setBusqueda] = useState('');
  const texto = busqueda.trim().toLowerCase();
  const visibles = fichas
    .filter(FILTROS[filtro].aplica)
    .filter(f => !texto || f.dni.includes(texto.replace(/\D/g, '') || texto) || `${f.apellido} ${f.nombre}`.toLowerCase().includes(texto))
    .sort((a, b) => Number(b.conObservaciones) - Number(a.conObservaciones));

  return (
    <div className="card">
      <div className="card-header flex flex-wrap items-center justify-between gap-3">
        {acciones.soloLectura && (
          <div className="flex w-full items-center justify-between">
            <h2 className="font-semibold text-gray-900">Fichas presentadas</h2>
            <span className="badge-gray">Solo lectura</span>
          </div>
        )}
        <div className="flex flex-wrap gap-1">
          {(Object.keys(FILTROS) as Filtro[]).map(clave => (
            <button
              key={clave}
              onClick={() => onFiltro(clave)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                filtro === clave ? 'bg-white text-primary-700 shadow-sm ring-1 ring-gray-200' : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {FILTROS[clave].etiqueta}
              {!sinContadores && (
                <span className="ml-1.5 text-xs text-gray-400">{fichas.filter(FILTROS[clave].aplica).length}</span>
              )}
            </button>
          ))}
        </div>
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por DNI o apellido"
            className="input w-64 py-2 pl-9"
          />
        </div>
      </div>

      {visibles.length === 0 ? (
        <p className="px-6 py-10 text-center text-sm text-gray-500">No hay fichas que coincidan con el filtro.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>DNI</th>
                <th>Apellido y nombre</th>
                <th>Fecha de la ficha</th>
                <th>Resultado del control</th>
                {!acciones.soloLectura && <th className="text-right">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {visibles.map(f => <FilaFicha key={f.id} ficha={f} {...acciones} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FilaFicha({ ficha, soloLectura, onEditar, onQuitar, onConstancia, subiendoConstancia }: PropsFila) {
  const inputConstancia = useRef<HTMLInputElement>(null);
  const observada = ficha.conObservaciones;
  const elegirConstancia = () => inputConstancia.current?.click();

  return (
    <tr className={`align-top ${observada ? 'bg-amber-50/40' : ''}`}>
      <td className={`border-l-4 font-medium tabular-nums ${observada ? 'border-l-amber-400' : 'border-l-green-400'}`}>
        {formatearDni(ficha.dni)}
      </td>
      <td>
        <p className="font-medium text-gray-900">{ficha.apellido}</p>
        <p className="text-xs text-gray-500">{ficha.nombre}</p>
      </td>
      <td className="tabular-nums">{formatFechaCalendario(ficha.fechaAfiliacion)}</td>
      <td className="min-w-[24rem] !whitespace-normal">
      <div className="space-y-2">
          {ficha.resultado && <ResultadoFicha resultado={ficha.resultado} />}
        {observada ? (
            <span className="badge-warning gap-1">
              <ExclamationTriangleIcon className="h-3.5 w-3.5" /> Con observaciones
            </span>
          ) : ficha.mensajes.length > 0 ? (
            <span className="badge-info gap-1">
              <InformationCircleIcon className="h-3.5 w-3.5" /> Duplicado válido
            </span>
          ) : (
            <span className="badge-success gap-1">
              <CheckCircleIcon className="h-3.5 w-3.5" /> Sin observaciones
            </span>
          )}

          {ficha.mensajes.length > 0 && (
            <ul className="space-y-1">
              {ficha.mensajes.map((m, i) => (
                <li key={i} className={`flex gap-2 text-sm ${observada ? 'text-amber-900' : 'text-gray-600'}`}>
                  {observada ? (
                    <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-500" />
                  ) : (
                    <InformationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" />
                  )}
                  <span>{m}</span>
                </li>
              ))}
            </ul>
          )}

          {ficha.admiteConstancia && (
            <>
              {!soloLectura && (
                <input
                  ref={inputConstancia}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(e) => {
                    const archivo = e.target.files?.[0];
                    if (archivo) onConstancia(ficha.id, archivo);
                    e.target.value = '';
                  }}
                />
              )}
              {ficha.constanciaAdjunta ? (
                <div className="flex items-center gap-2 rounded-lg border border-primary-100 bg-primary-50 px-3 py-2 text-xs text-primary-800">
                  <PaperClipIcon className="h-4 w-4 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">Constancia adjunta · pendiente de revisión de la SEP</p>
                    {ficha.constanciaNombre && (
                      <p className="max-w-xs truncate text-primary-700" title={ficha.constanciaNombre}>{ficha.constanciaNombre}</p>
                    )}
                  </div>
                  {!soloLectura && (
                    <button
                      onClick={elegirConstancia}
                      disabled={subiendoConstancia}
                      className="flex-shrink-0 font-medium text-primary-700 underline hover:text-primary-900 disabled:opacity-50"
                    >
                      Reemplazar
                    </button>
                  )}
                </div>
              ) : soloLectura ? (
                <p className="text-xs text-gray-500">Presentada sin constancia de renuncia.</p>
              ) : (
                <button onClick={elegirConstancia} disabled={subiendoConstancia} className="btn-secondary gap-1.5 px-3 py-1.5 text-xs">
                  <PaperClipIcon className="h-4 w-4" /> Adjuntar constancia de renuncia
                </button>
              )}
            </>
          )}
        </div>
      </td>
      {!soloLectura && (
        <td className="text-right">
          <div className="inline-flex gap-1">
            <button onClick={() => onEditar(ficha)} title="Editar" className="rounded-lg p-2 text-gray-500 hover:bg-primary-50 hover:text-primary-600">
              <PencilSquareIcon className="h-5 w-5" />
            </button>
            <button onClick={() => onQuitar(ficha)} title="Quitar" className="rounded-lg p-2 text-gray-500 hover:bg-red-50 hover:text-red-600">
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        </td>
      )}
    </tr>
  );
}

function Campo({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

function BotonesModal({ onCancelar, guardando }: { onCancelar: () => void; guardando: boolean }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" onClick={onCancelar} className="btn-secondary">Cancelar</button>
      <button type="submit" disabled={guardando} className="btn-primary">{guardando ? 'Guardando…' : 'Guardar'}</button>
    </div>
  );
}

function ModalEdicion({ ficha, onCerrar, onGuardado }: { ficha: FichaLote; onCerrar: () => void; onGuardado: (l: LoteAfiliacion) => void }) {
  const [form, setForm] = useState({
    dni: ficha.dni,
    apellido: ficha.apellido,
    nombre: ficha.nombre,
    fechaNacimiento: ficha.fechaNacimiento?.slice(0, 10) ?? '',
    fechaAfiliacion: ficha.fechaAfiliacion?.slice(0, 10) ?? '',
  });

  const guardar = useMutation({
    mutationFn: () => {
      const cambios: EdicionFichaPayload = {};
      if (form.dni !== ficha.dni) cambios.dni = form.dni;
      if (form.apellido !== ficha.apellido) cambios.apellido = form.apellido;
      if (form.nombre !== ficha.nombre) cambios.nombre = form.nombre;
      if (form.fechaNacimiento && form.fechaNacimiento !== ficha.fechaNacimiento?.slice(0, 10)) {
        cambios.fechaNacimiento = form.fechaNacimiento;
      }
      if (form.fechaAfiliacion && form.fechaAfiliacion !== ficha.fechaAfiliacion?.slice(0, 10)) {
        cambios.fechaAfiliacion = form.fechaAfiliacion;
      }
      return loteAfiliacionService.editarFicha(ficha.id, cambios);
    },
    onSuccess: (l) => {
      toast.success('Ficha actualizada.');
      onGuardado(l);
    },
  });

  return (
    <Modal isOpen onClose={onCerrar} title="Editar ficha" description="Corregí los datos y se vuelve a controlar en el momento." size="md">
      <form onSubmit={(e) => { e.preventDefault(); guardar.mutate(); }} className="space-y-4">
        <Campo label="DNI">
          <input value={form.dni} onChange={(e) => setForm({ ...form, dni: e.target.value })} className="input" required />
        </Campo>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Apellido">
            <input value={form.apellido} onChange={(e) => setForm({ ...form, apellido: e.target.value })} className="input" required />
          </Campo>
          <Campo label="Nombre">
            <input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="input" required />
          </Campo>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Fecha de nacimiento">
            <input type="date" value={form.fechaNacimiento} onChange={(e) => setForm({ ...form, fechaNacimiento: e.target.value })} className="input" required />
          </Campo>
          <Campo label="Fecha de la ficha">
            <input type="date" value={form.fechaAfiliacion} onChange={(e) => setForm({ ...form, fechaAfiliacion: e.target.value })} className="input" required />
          </Campo>
        </div>
        <BotonesModal onCancelar={onCerrar} guardando={guardar.isPending} />
      </form>
    </Modal>
  );
}

function ModalAlta({ loteId, onCerrar, onGuardado }: { loteId: string; onCerrar: () => void; onGuardado: (l: LoteAfiliacion) => void }) {
  const [form, setForm] = useState<AltaFichaPayload>({
    dni: '', apellido: '', nombre: '', fechaNacimiento: '', fechaAfiliacion: '',
  });

  const guardar = useMutation({
    mutationFn: () => loteAfiliacionService.agregarFicha(loteId, form),
    onSuccess: (l) => {
      toast.success('Ficha agregada.');
      onGuardado(l);
    },
  });

  return (
    <Modal isOpen onClose={onCerrar} title="Agregar ficha" description="Se controla contra el padrón apenas la guardás." size="md">
      <form onSubmit={(e) => { e.preventDefault(); guardar.mutate(); }} className="space-y-4">
        <Campo label="DNI">
          <input value={form.dni} onChange={(e) => setForm({ ...form, dni: e.target.value })} className="input" required />
        </Campo>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Apellido">
            <input value={form.apellido} onChange={(e) => setForm({ ...form, apellido: e.target.value })} className="input" required />
          </Campo>
          <Campo label="Nombre">
            <input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="input" required />
          </Campo>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Fecha de nacimiento">
            <input type="date" value={form.fechaNacimiento} onChange={(e) => setForm({ ...form, fechaNacimiento: e.target.value })} className="input" required />
          </Campo>
          <Campo label="Fecha de la ficha">
            <input type="date" value={form.fechaAfiliacion} onChange={(e) => setForm({ ...form, fechaAfiliacion: e.target.value })} className="input" required />
          </Campo>
        </div>
        <BotonesModal onCancelar={onCerrar} guardando={guardar.isPending} />
      </form>
    </Modal>
  );
}

function ResultadoFicha({ resultado }: { resultado: NonNullable<FichaLote['resultado']> }) {
  const { etiqueta, clase } = RESULTADO_FICHA[resultado.estado];
  return <span className={clase}>{etiqueta}</span>;
}